<template>
  <div id="page-product-form">
    <vs-row>
      <vs-col :class="readonly ? 'readonly-form' : ''">
        <vue-element-loading :active="busy"></vue-element-loading>
        <card-page card-title="Contact Details" :switch-model="IsEnabled">
          <vue-form-generator
            ref="formDetails"
            :model="model"
            :options="formOptions"
            :schema="schemaContactDetails"
          />
        </card-page>
        <card-page card-title="Class of Business">
          <additive-widget
            ref="formClassOfBusiness"
            v-model="model"
            :form-options="formOptions"
            :schema="schemaClassOfBusiness"
            label="Classes of Business"
          />
        </card-page>
        <card-page card-title="Email Addresses">
          <additive-widget
            ref="formEmailAddresses"
            v-model="model"
            :form-options="formOptions"
            :schema="schemaEmails"
            label="Email Addresses"
            :readonly="readonly"
            primary-toggle
            primary-field-name="IsPrimaryEmail"
            mandatory
          />
        </card-page>
        <card-page card-title="Contact Numbers">
          <additive-widget
            ref="formContactNumbers"
            v-model="model"
            :form-options="formOptions"
            :schema="schemaContactNumbers"
            label="Contact Numbers"
            :readonly="readonly"
            primary-toggle
            primary-field-name="IsPrimaryNumber"
            mandatory
          />
        </card-page>
        <card-page card-title="External Links">
          <additive-widget
            ref="formExternalLinks"
            v-model="model"
            :form-options="formOptions"
            :schema="schemaExternalLinks"
            label="External Links"
          />
        </card-page>
      </vs-col>
    </vs-row>
  </div>
</template>

<script>
import CardPage from '../../../components/Views/CardPage';
import FormModelMixin from '../../../../components/mixins/FormModelMixin';
import FormFieldValidationMixin from '../../../../components/mixins/FormFieldValidationMixin';
import AdditiveWidget from '../../../../components/form/fields/AdditiveWidget';
import {arrayOptions, selectOptions} from '@/components/form/FormElementOption';
import CountryList from 'countries-list';
import UserDetailsForm from './UserDetailsForm';

export default {
  name: 'UserProfileCard',
  components: {
    AdditiveWidget,
    CardPage,
    UserDetailsForm
  },
  mixins: [FormModelMixin, FormFieldValidationMixin],
  props: {
    value: {
      type: Object,
      required: true
    },
    readonly: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data() {
    return {
      model: {
        IsEnabled: 0,
        class_of_business: [],
        email_addresses: [{
          EmailAddress: null,
          EmailType_FK: null,
          IsPrimaryEmail: null,
        }],
        phone_numbers: [{
          PhoneType_FK: null,
          CountryCode: null,
          PhoneNumber: null,
          PhoneExtension: null,
          IsPrimaryNumber: null,
        }],
        external_links: [],
        profile: {},
      },
      formReferences: ['formDetails'],
      additiveForms: [
        'formClassOfBusiness',
        'formEmailAddresses',
        'formContactNumbers',
        'formExternalLinks'
      ],
      schemaContactDetails: {
        groups: [
          {
            styleClasses: 'w-full mt-0',
            legend: 'General',
            fields: [
              {
                disabled: () => this.readonly,
                type: 'vueMultiSelect',
                inputType: 'text',
                styleClasses: 'w-1/3',
                label: 'Title',
                model: 'Title',
                values: ['Dr', 'Mr', 'Mrs', 'Ms', 'Miss', 'Professor'],
                selectOptions: {
                  showLabels: false,
                  hideSelected: false
                },
                required: () => !this.readonly,
                validator: 'required'
              }
            ]
          },
          {
            styleClasses: 'w-full mt-0',
            fields: [
              {
                disabled: () => this.readonly,
                type: 'input',
                inputType: 'text',
                styleClasses: 'w-1/3',
                label: 'First Name',
                model: 'FirstName',
                required: () => !this.readonly,
                validator: ['required', 'string']
              },
              {
                disabled: () => this.readonly,
                type: 'input',
                inputType: 'text',
                styleClasses: 'w-1/3',
                label: 'Middle Name(s)',
                model: 'MiddleName',
                validator: 'string'
              },
              {
                disabled: () => this.readonly,
                type: 'input',
                inputType: 'text',
                styleClasses: 'w-1/3',
                label: 'Last Name',
                model: 'LastName',
                required: () => !this.readonly,
                validator: ['required', 'string']
              }
            ]
          },
          {
            styleClasses: 'w-full mt-0',
            fields: [
              {
                disabled: () => this.readonly,
                type: 'vueMultiSelect',
                inputType: 'text',
                styleClasses: 'w-1/3',
                label: 'Associated Company',
                model: 'Company_FK',
                values: () => this.asyncCompanyOptions,
                required: true,
                validator: ['required'],
                placeholder: "Type to search",
                selectOptions: {
                  ...selectOptions,

                  // Async Search
                  hideSelected: false,
                  searchable: true,
                  localSearch: false,
                  internalSearch: false,
                  clearOnSelect: false,
                  onSearch: function(searchQuery, id, options) {
                    if (searchQuery.length === 0) {
                      this.asyncCompanyOptions = []
                    } else {
                      this.asyncCompanyOptions = this.companyOptions.filter((element, index, array) => {
                        return element.name.toLowerCase().includes(searchQuery.toLowerCase())
                      })
                    }
                  }.bind(this),
                },
              },
              {
                disabled: () => this.readonly,
                type: 'input',
                inputType: 'text',
                styleClasses: 'w-1/3',
                label: 'Job Title',
                model: 'profile.JobTitle'
              },
              {
                disabled: () => this.readonly,
                type: 'input',
                inputType: 'text',
                styleClasses: 'w-1/3',
                label: 'Department',
                model: 'profile.Department'
              },
              {
                type: 'vsCheckbox',
                styleClasses: 'w-1/3',
                label: 'Main',
                model: 'Main',
                disabled: () => this.readonly
              },
            ]
          },
          {
            styleClasses: 'w-full mt-0',
            legend: 'Profile',
            fields: [
              {
                disabled: () => this.readonly,
                type: 'vueMultiSelect',
                inputType: 'text',
                styleClasses: 'w-1/3',
                label: 'FISG Role',
                model: 'profile.FisgRoleType_FK',
                values: () => this.fisgRoleOptions,
                selectOptions,
                required: () => !this.readonly,
                validator: 'required'
              },
              {
                disabled: () => this.readonly,
                type: 'vsCheckbox',
                styleClasses: 'vx-col w-1/3',
                label: 'Marketing Preference',
                model: 'profile.MarketingPreference'
              },
              {
                disabled: () => this.readonly,
                type: 'vsCheckbox',
                styleClasses: 'vx-col w-1/3',
                label: 'Retainer',
                model: 'profile.Retainer'
              },
              {
                disabled: () => this.readonly,
                type: 'vueMultiSelect',
                inputType: 'text',
                styleClasses: 'w-1/3',
                label: 'Impersonated Company',
                model: 'profile.ImpersonatedCompany',
                values: () => this.asyncImpersonatedCompanyOptions,
                placeholder: "Type to search",
                selectOptions: {
                  ...selectOptions,

                  // Async Search
                  hideSelected: false,
                  searchable: true,
                  localSearch: false,
                  internalSearch: false,
                  clearOnSelect: false,
                  onSearch: function(searchQuery, id, options) {
                    if (searchQuery.length === 0) {
                      this.asyncImpersonatedCompanyOptions = []
                    } else {
                      this.asyncImpersonatedCompanyOptions = this.impersonatedCompanyOptions.filter((element, index, array) => {
                        return element.name.toLowerCase().includes(searchQuery.toLowerCase())
                      })
                    }
                  }.bind(this),
                },
              },
              {
                disabled: () => this.readonly,
                type: 'vsCheckbox',
                styleClasses: 'vx-col w-1/3',
                label: 'Impersonate Mode',
                model: 'profile.IsImpersonation'
              },
            ]
          }
        ]
      },
      schemaClassOfBusiness: {
        groups: [
          {
            styleClasses: 'w-full',
            fields: [
              {
                model: 'class_of_business',
                ...arrayOptions,
                hideAddButton: this.readonly,
                showRemoveButton: !this.readonly,
                items: {
                  type: 'object',
                  styleClasses: 'w-full',
                  schema: {
                    groups: [
                      {
                        styleClasses: 'w-full',
                        fields: [
                          {
                            disabled: () => this.readonly,
                            type: 'vueMultiSelect',
                            inputType: 'text',
                            styleClasses: 'w-1/3',
                            label: 'Class of Business',
                            model: 'ClassOfBusiness_FK',
                            values: () => this.classesOfBusiness,
                            selectOptions,
                            required: () => !this.readonly,
                            validator: !this.readonly ? 'required' : ''
                          }
                        ]
                      }
                    ]
                  }
                }
              }
            ]
          }
        ]
      },
      schemaEmails: {
        groups: [
          {
            styleClasses: 'w-full',
            fields: [
              {
                model: 'email_addresses',
                ...arrayOptions,
                hideAddButton: this.readonly,
                showRemoveButton: !this.readonly,
                items: {
                  type: 'object',
                  styleClasses: 'w-full',
                  schema: {
                    groups: [
                      {
                        styleClasses: 'w-full',
                        fields: [
                          {
                            disabled: () => this.readonly,
                            type: 'vueMultiSelect',
                            inputType: 'text',
                            label: 'Email Type',
                            styleClasses: 'w-1/3',
                            model: 'EmailType_FK',
                            values: () => this.emailTypeOptions,
                            selectOptions,
                            required: () => !this.readonly,
                            validator: 'required'
                          },
                          {
                            disabled: () => this.readonly,
                            type: 'input',
                            inputType: 'text',
                            label: 'Email Address',
                            styleClasses: 'w-1/3',
                            model: 'EmailAddress',
                            required: () => !this.readonly,
                            validator: ['required', 'string']
                          },
                        ]
                      }
                    ]
                  }
                }
              }
            ]
          }
        ]
      },
      schemaContactNumbers: {
        groups: [
          {
            styleClasses: 'w-full',
            fields: [
              {
                model: 'phone_numbers',
                ...arrayOptions,
                hideAddButton: this.readonly,
                showRemoveButton: !this.readonly,
                items: {
                  type: 'object',
                  styleClasses: 'w-full',
                  schema: {
                    groups: [
                      {
                        styleClasses: 'w-full',
                        fields: [
                          {
                            disabled: () => this.readonly,
                            type: 'vueMultiSelect',
                            inputType: 'text',
                            styleClasses: 'w-1/3',
                            label: 'Phone Type',
                            model: 'PhoneType_FK',
                            values: () => this.contactNumberTypeOptions,
                            selectOptions,
                            required: () => !this.readonly,
                            validator: 'required'
                          },
                          {
                            disabled: () => this.readonly,
                            type: 'vueMultiSelect',
                            inputType: 'text',
                            styleClasses: 'w-1/3',
                            label: 'Country Code',
                            model: 'CountryCode',
                            values: () => this.countryOptions,
                            selectOptions,
                            required: () => !this.readonly,
                            validator: 'required'
                          },{
                            type: 'label',
                            styleClasses: 'w-1/3',
                          },
                          {
                            disabled: () => this.readonly,
                            type: 'input',
                            inputType: 'text',
                            styleClasses: 'w-1/3 pt-4',
                            label: 'Phone Number',
                            model: 'PhoneNumber',
                            required: () => !this.readonly,
                            validator: [
                              'required',
                              'string',
                              model => {
                                const max = 25;

                                if (isNaN(model)) {
                                  return 'Value must be a number';
                                }

                                if (model && model.length > max) {
                                  return `Value must be lower than ${max}`;
                                }
                              }
                            ]
                          },
                          {
                            disabled: () => this.readonly,
                            type: 'input',
                            inputType: 'text',
                            styleClasses: 'w-1/3 pt-4',
                            label: 'Extension',
                            model: 'PhoneExtension',
                            validator: 'string',
                          },
                        ]
                      }
                    ]
                  }
                }
              }
            ]
          }
        ]
      },
      schemaExternalLinks: {
        groups: [
          {
            styleClasses: 'w-full pt-3',
            fields: [
              {
                model: 'external_links',
                ...arrayOptions,
                hideAddButton: this.readonly,
                showRemoveButton: !this.readonly,
                items: {
                  type: 'object',
                  styleClasses: 'w-full',
                  schema: {
                    groups: [
                      {
                        styleClasses: 'w-full',
                        fields: [
                          {
                            disabled: () => this.readonly,
                            type: 'vueMultiSelect',
                            inputType: 'text',
                            label: 'Link Type',
                            styleClasses: 'w-1/3',
                            model: 'LinkType_FK',
                            values: () => this.ExternalLinkTypeOptions,
                            selectOptions,
                            required: () => !this.readonly,
                            validator: 'required'
                          },
                          {
                            disabled: () => this.readonly,
                            type: 'input',
                            inputType: 'text',
                            label: 'Link',
                            styleClasses: 'w-1/3',
                            model: 'ExternalLink',
                            required: () => !this.readonly,
                            validator: ['required', 'string']
                          }
                        ]
                      }
                    ]
                  }
                }
              }
            ]
          }
        ]
      },
      formOptions: {
        validateAfterLoad: false,
        validateAfterChanged: true
      },
      asyncCompanyOptions: [],
      asyncImpersonatedCompanyOptions: [],
      companyOptions: [],
      impersonatedCompanyOptions: [],
      fisgRoleOptions: [],
      classesOfBusiness: [],
      emailTypeOptions: [],
      contactNumberTypeOptions: [],
      ExternalLinkTypeOptions: [],
      options: [
        {
          endpoint: () => this.$api.companies.all(),
          optionsFieldName: 'companyOptions',
          nameFieldName: 'name',
          idFieldName: 'Company_FK',
        },
        {
          endpoint: () => this.$api.companies.all(),
          optionsFieldName: 'impersonatedCompanyOptions',
          nameFieldName: 'name',
          idFieldName: 'ImpersonatedCompany',
          modelArrayName: 'profile'
        },
        {
          endpoint: () => this.$api.userProfile.getRoleTypes(),
          optionsFieldName: 'fisgRoleOptions',
          nameFieldName: 'Type',
          idFieldName: 'FisgRoleType_FK',
          modelArrayName: 'profile'
        },
        {
          endpoint: () => this.$api.facilityClassOfBusiness.list(),
          optionsFieldName: 'classesOfBusiness',
          nameFieldName: 'Description',
          idFieldName: 'ClassOfBusiness_FK',
          modelArrayName: 'class_of_business'
        },
        {
          endpoint: () => this.$api.userEmailAddress.getOptions(),
          optionsFieldName: 'emailTypeOptions',
          nameFieldName: 'Type',
          idFieldName: 'EmailType_FK',
          modelArrayName: 'email_addresses'
        },
        {
          endpoint: () => this.$api.companyAddressPhoneNumber.getOptions(),
          optionsFieldName: 'contactNumberTypeOptions',
          nameFieldName: 'Type',
          idFieldName: 'PhoneType_FK',
          modelArrayName: 'phone_numbers'
        },
        {
          endpoint: () => this.$api.userExternalLink.getOptions(),
          optionsFieldName: 'ExternalLinkTypeOptions',
          nameFieldName: 'Type',
          idFieldName: 'LinkType_FK',
          modelArrayName: 'external_links'
        }
      ],
      countryOptions: []
    };
  },
  computed: {
    IsEnabled() {
      if (this.model) {
        return this.model.IsEnabled;
      }

      return 0;
    }
  },
  async created() {
    await this.setCountryOptions();
  },
  methods: {
    async setCountryOptions() {
      const countryList = [];

      await new Promise(resolve => setTimeout(resolve, 1000));

      for (const [key, country] of Object.entries(CountryList.countries)) {
        countryList.push({
          id: parseInt(country.phone),
          name: country.name
        });
      }

      if (this.model && this.model.phone_numbers.length) {
        const phoneNumbers = this.value.phone_numbers;

        phoneNumbers.forEach(phoneNumber => {
          phoneNumber.CountryCode = countryList.find(
            ({ id }) => parseInt(phoneNumber.CountryCode) === id
          );
        });
      }

      this.countryOptions = countryList;
    }
  }
};
</script>
